import {
  useCallback,
} from 'react';
import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
  ResourceType,
} from '@gymcloud/core';

import {
  updateItemPositonOnList,
} from 'web-utils';
import {
  uiActions,
} from 'web-actions';

const useProgramWorkout = ({
  workoutId,
  programId,
  programWorkoutId,
  resourceType,
}: {
  workoutId: number,
  programId: number,
  programWorkoutId: number,
  resourceType: ResourceType,
}) => {
  const dispatch = useDispatch();

  const mapState = useCallback(state => ({
    workout: coreSelectors.getResourceById(
      resourceType,
      workoutId,
    )(state),
    programWorkout: coreSelectors.getResourceById(
      'programWorkouts',
      programWorkoutId,
    )(state),
    programWorkouts: coreSelectors.getResourceMappedList(
      'programWorkouts',
      `program_${programId}`,
    )(state),
  }), [workoutId, programId]);
  const {
    workout,
    programWorkout,
    programWorkouts,
  } = useSelector(mapState, shallowEqual);

  const updateProgramWorkoutsPosition = (listFactory: any) => {
    const newList = Array.from(
      programWorkouts
        .filter(w => w.week_id === programWorkout.week_id)
        .sort((a, b) => (a.position > b.position ? 1 : -1)),
    );

    const resources = updateItemPositonOnList(listFactory(newList));

    dispatch(
      coreActions.updateResources({
        resourceType: 'programWorkouts',
        resources,
      }),
    );

    dispatch(
      coreActions.requestUpdateItems({
        resources,
        resourceType: 'programWorkouts',
      }),
    );
  };

  const saveWorkout = () => {
    const onSelect = (values, actions) => {
      const callbacks = {
        ...actions,
        onSuccess: () => {
          dispatch(
            uiActions.calcelMoveEntityModal(),
          );
        },
      };

      if (resourceType === 'personalWorkouts') {
        dispatch(
          coreActions.requestSaveAsTemplate({
            id: programWorkout.id,
            payload: {
              folder_id: values.folderId,
            },
            resourceType: 'workoutTemplates',
            callbacks,
          }),
        );
      } else {
        dispatch(
          coreActions.requestDuplicateEntity({
            ids: [workout.workout_template_id || workout.id],
            folderIds: [Number(values.folderId)],
            resourceType: 'workoutTemplates',
            callbacks,
          }),
        );
      }
    };

    dispatch(
      uiActions.moveEntityModal({
        modal: {
          title: 'Save to Folder',
          helper: 'Select folder to save Workout template(s) to',
          buttonText: 'Save',
          folderName: 'Workout Templates',
        },
        onSelect,
      }),
    );
  };

  const duplicateWorkout = () => {
    const onSuccess = (data) => {
      // After duplicate the workout we need to update rest workout position
      updateProgramWorkoutsPosition((newList) => {
        const originalIndex = newList.findIndex(i => i.id === programWorkout.id);
        newList.splice(originalIndex + 1, 0, data);
        return newList;
      });
    };

    if (resourceType === 'personalWorkouts') {
      dispatch(
        coreActions.requestDuplicatePersonalProgramWorkout({
          id: programWorkout.id,
          payload: {
            week_id: programWorkout.week_id,
            position: programWorkout.position + 1,
          },
          callbacks: {
            onSuccess,
          },
        }),
      );
    } else {
      dispatch(
        coreActions.requestAddProgramWorkout({
          payload: {
            week_id: programWorkout.week_id,
            position: programWorkout.position + 1,
            program_id: programWorkout.program_id,
            program_type: 'ProgramTemplate',

            workout_template_id: workoutId,
          },
          callbacks: {
            onSuccess,
          },
        }),
      );
    }
  };

  const deleteWorkout = () => {
    const onConfirmAction = coreActions.requestDeleteEntity({
      id: programWorkout.id,
      resourceType: 'programWorkouts',
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete Program Workout?',
          bodyText: `Are you sure you want to delete <b>${workout.name}</b>?`,
          confirmText: 'Delete',
        },
        onSuccess: () => {
          // After delete the workout we need to update rest workout position
          updateProgramWorkoutsPosition(
            (list) => list.filter(p => p.id !== programWorkout.id),
          );
        },
        onConfirmAction,
      }),
    );
  };

  return {
    saveWorkout,
    deleteWorkout,
    duplicateWorkout,
  };
};

export default useProgramWorkout;
